import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@apollo/client';
import { Loader2Icon } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogFooter,
  Form,
  FormField,
  FormControl,
  FormItem,
  FormMessage,
  Textarea,
} from '@sandtech-hub-monorepo/ui-kit';

import { UpdateUserDocument } from '@sandtech-hub-monorepo/ehub-client';

const formSchema = z.object({
  about: z
    .string()
    .max(500, { message: 'Bio must not exceed 500 characters!' })
    .optional(),
});

export const AboutSectionEditDialog = ({
  currentValues,
  open,
  handleOpenChange,
}: {
  currentValues: z.infer<typeof formSchema>;
  open: boolean;
  handleOpenChange: (open: boolean) => void;
}) => {
  const [updateUser, { loading }] = useMutation(UpdateUserDocument);

  const handleSubmit = async ({ about }: { about?: string }) => {
    await updateUser({
      variables: {
        input: {
          bio: about,
        },
      },
    });

    handleOpenChange(false);
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      about: currentValues.about,
    },
  });

  useEffect(() => {
    form.reset({ about: currentValues.about });
  }, [currentValues, form]);

  return (
      <Dialog open={open} onOpenChange={handleOpenChange} title="About" description="Tell us something about you" className="bg-card">
        <Form {...form}>
          <form
            className="flex flex-col flex-1 justify-between"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="about"
              render={({ field }) => (
                <FormItem className="flex flex-col flex-1 gap-2">
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Write a brief introduction to show on your profile..."
                      className="h-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-1">
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2Icon className="animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </Dialog>
  );
};
